<script lang="ts">
    import { Input } from '@/components/ui/input';
    import { Button } from '@/components/ui/button';
    import * as Popover from '@/components/ui/popover';
    import { Search, SlidersHorizontal, X, LoaderCircle, Clock, Calendar, CalendarPlus } from 'lucide-svelte';
    import LeadKanbanAdvancedFilters from './LeadKanbanAdvancedFilters.svelte';
    import type { LeadFilters } from './kanban-filters.svelte';

    type SortType = 'updated_at' | 'estimated_job_start' | 'created_at';

    type Props = {
        filters: LeadFilters;
        isLoading: boolean;
        activeFilterCount: number;
        sortBy: SortType;
        onFilterChange: () => void;
        onClearFilters: () => void;
        onSortChange: (sort: SortType) => void;
    };

    let { filters = $bindable(), isLoading, activeFilterCount, sortBy, onFilterChange, onClearFilters, onSortChange }: Props = $props();

    let popoverOpen = $state(false);

    function handleSearchInput() {
        onFilterChange();
    }

    function handleApplyAdvanced() {
        onFilterChange();
    }

    function handleClearAll() {
        onClearFilters();
        popoverOpen = false;
    }
</script>

<div class="filter-bar">
    <div class="search-container">
        <Search class="search-icon" />
        <Input
            type="text"
            placeholder="Search leads by ref, name, organisation, email, address, postcode..."
            bind:value={filters.search}
            oninput={handleSearchInput}
            class="search-input"
        />
        {#if isLoading}
            <LoaderCircle class="loading-icon animate-spin" />
        {/if}
    </div>

    <div class="filter-actions">
        <Popover.Root bind:open={popoverOpen}>
            <Popover.Trigger>
                <Button variant="outline" class="filter-button">
                    <SlidersHorizontal class="h-4 w-4" />
                    <span>Filters</span>
                    {#if activeFilterCount > 0}
                        <span class="filter-badge">{activeFilterCount}</span>
                    {/if}
                </Button>
            </Popover.Trigger>
            <Popover.Content align="end" class="w-[420px] p-0">
                <LeadKanbanAdvancedFilters
                    bind:filters
                    onApply={handleApplyAdvanced}
                    onClear={handleClearAll}
                    onClose={() => popoverOpen = false}
                />
            </Popover.Content>
        </Popover.Root>

        {#if activeFilterCount > 0}
            <Button variant="ghost" size="sm" onclick={handleClearAll} class="clear-button">
                <X class="h-4 w-4" />
                <span>Clear</span>
            </Button>
        {/if}
    </div>
    <div class="sort-section">
        <span class="sort-label">Sort:</span>
        <Button
            variant={sortBy === 'updated_at' ? 'default' : 'outline'}
            size="sm"
            onclick={() => onSortChange('updated_at')}
            class="sort-button"
        >
            <Clock class="h-4 w-4" />
            <span>Updated</span>
        </Button>
        <Button
            variant={sortBy === 'estimated_job_start' ? 'default' : 'outline'}
            size="sm"
            onclick={() => onSortChange('estimated_job_start')}
            class="sort-button"
        >
            <Calendar class="h-4 w-4" />
            <span>Job Start</span>
        </Button>
        <Button
            variant={sortBy === 'created_at' ? 'default' : 'outline'}
            size="sm"
            onclick={() => onSortChange('created_at')}
            class="sort-button"
        >
            <CalendarPlus class="h-4 w-4" />
            <span>Created</span>
        </Button>
    </div>
</div>


<style>
    .filter-bar {
        display: flex;
        align-items: center;
        gap: 1rem;
        background: hsl(var(--card));
        border: 1px solid hsl(var(--border));
        border-radius: var(--radius);
    }

    .search-container {
        position: relative;
        flex: 1;
        max-width: 500px;
    }

    .search-container :global(.search-icon) {
        position: absolute;
        left: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        width: 1rem;
        height: 1rem;
        color: hsl(var(--muted-foreground));
        pointer-events: none;
    }

    .search-container :global(.search-input) {
        padding-left: 2.25rem;
        padding-right: 2.25rem;
    }

    .search-container :global(.loading-icon) {
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        width: 1rem;
        height: 1rem;
        color: hsl(var(--muted-foreground));
    }

    .filter-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .filter-actions :global(.filter-button) {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .filter-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.25rem;
        height: 1.25rem;
        padding: 0 0.375rem;
        font-size: 0.75rem;
        font-weight: 600;
        background: hsl(var(--primary));
        color: hsl(var(--primary-foreground));
        border-radius: 9999px;
    }

    .filter-actions :global(.clear-button) {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        color: hsl(var(--muted-foreground));
    }

    .filter-actions :global(.clear-button:hover) {
        color: hsl(var(--destructive));
    }

    .sort-section {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .sort-label {
        font-size: 0.875rem;
        color: hsl(var(--muted-foreground));
        white-space: nowrap;
    }

    .sort-section :global(.sort-button) {
        display: flex;
        align-items: center;
        gap: 0.375rem;
    }
</style>
